/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { LocationUrls } from "./LocationUrls";
import { ScreenControllerTypes } from "./ScreenControllerTypes";

/**
 * Maps every known URL path to its canonical ScreenController name.
 *
 * This is the single source of truth for URL → screen-name derivation.
 * Used by:
 *   - screensSlice.syncScreenFromUrl (sets screenController for D-pad engine)
 *   - NavigationInterceptor (sets activeScreen on every navigation transition)
 *   - navigationStackSlice (records screen name in the nav stack)
 */
const URL_TO_SCREEN_NAME: Readonly<Record<string, string>> = {
  [LocationUrls.SPLASH]: ScreenControllerTypes.SPLASH_SCREEN,
  [LocationUrls.SIGN_IN]: ScreenControllerTypes.SIGN_IN_SCREEN,
  [LocationUrls.HOME_SCREEN]: ScreenControllerTypes.HOME_SCREEN,
  [LocationUrls.GUIDE]: ScreenControllerTypes.GUIDE_SCREEN,
  [LocationUrls.SELECT_PROFILE]: ScreenControllerTypes.SELECT_PROFILE_SCREEN,
  [LocationUrls.PROFILES]: ScreenControllerTypes.MANAGE_PROFILES_SCREEN,
  [LocationUrls.PROFILE_DETAILS]: ScreenControllerTypes.PROFILE_DETAILS_SCREEN,
  [LocationUrls.CONTENT_DETAIL]: ScreenControllerTypes.CONTENT_DETAIL_SCREEN,
  [LocationUrls.MY_HUB]: ScreenControllerTypes.MY_HUB_SCREEN,
  [LocationUrls.MY_HUB_MANAGE_SCREEN]:
    ScreenControllerTypes.MANAGE_SERIES_RECORDINGS_SCREEN,
  // The two Scheduled-tab drill-ins reuse the manage-series screen, so they
  // reuse its controller too: a screen name identifies input-handling
  // behaviour, and all three surfaces have the same focus order (folder
  // buttons, sort control, then the rows). Only one is ever mounted at a time,
  // so one registration serves all three routes.
  [LocationUrls.MY_HUB_SCHEDULED_SERIES]:
    ScreenControllerTypes.MANAGE_SERIES_RECORDINGS_SCREEN,
  [LocationUrls.MY_HUB_SCHEDULED_SINGLE]:
    ScreenControllerTypes.MANAGE_SERIES_RECORDINGS_SCREEN,
  [LocationUrls.SPORTS_HUB]: ScreenControllerTypes.SPORTS_HUB_SCREEN,
  [LocationUrls.SHOWS_AND_MOVIES]:
    ScreenControllerTypes.SHOWS_AND_MOVIES_SCREEN,
  [LocationUrls.SEARCH]: ScreenControllerTypes.SEARCH_SCREEN,
  [LocationUrls.SEE_MORE]: ScreenControllerTypes.SEE_MORE_SCREEN,
  [LocationUrls.SETTINGS]: ScreenControllerTypes.SETTINGS_SCREEN
};

/**
 * Returns the ScreenController name for the given URL path.
 * Returns an empty string if the URL has no registered controller.
 *
 * @param url - An absolute pathname such as "/home" or "/guide".
 */
export function urlToScreenName(url: string): string {
  const name: string | undefined = URL_TO_SCREEN_NAME[url];
  return name !== undefined ? name : "";
}
